import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as sequtil from 'molbio/SeqUtil';
import {TranslationRegionGroupData} from 'molbio/translations/TranslationUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';

interface ReindexModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onClickReindex: (current: number, desired: number) => void;
  sequenceLength: number;
  currentIndex: number;
  translationRegionGroups: Array<TranslationRegionGroupData>;
}

/**
 * Modal to confirm reindexing a circular sequence.
 */
export default function ReindexModal({
  onRequestClose,
  onRequestSubmit,
  onClickReindex,
  sequenceLength,
  currentIndex,
  translationRegionGroups,
}: ReindexModalProps): JSX.Element {
  const [newDisplayIndex, setNewDisplayIndex] = useState<number | null>(1);
  const hasMultiRegionTranslations = translationRegionGroups.some((group) => group.translations.length > 1);

  return (
    <Modal
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Re-index', submit: true, disabled: newDisplayIndex == null},
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        const modelIndex = sequtil.displayToModel(newDisplayIndex!, 1);
        onClickReindex(currentIndex, modelIndex);
        onRequestSubmit();
      }}
      title="Re-index sequence"
    >
      {/*  Since we always order regions of a translation by start position, reindexing the sequence
      could result in the ordering of a multi-region translation, and thus the AA sequence it translates to,
      to be unexpectedly changed. */}
      {hasMultiRegionTranslations && (
        <div className={css(styles.translationWarning)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="This sequence contains translations with multiple regions. Re-indexing this sequence may result in the ordering of those regions to change."
          />
        </div>
      )}
      <p>
        Enter the desired location for the current base (e.g. enter 100 for the current base to become the
        100th base).
      </p>
      <div className={css(styles.indexInputs)}>
        <div className={css(styles.col)}>
          <FieldLabel label="Current location" />
          <p>{sequtil.modelToDisplay(currentIndex, 1)}</p>
        </div>
        <div className={css(styles.col)}>
          <FieldLabel label="New location" />
          <NumericInput
            value={newDisplayIndex}
            onChange={(e) => {
              setNewDisplayIndex(e.target.numericValue);
            }}
            min={1}
            max={sequenceLength + 1}
            required={true}
            validateInteger={true}
            focusOnMount={true}
          />
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  indexInputs: {
    display: 'flex',
  },
  col: {
    flex: 1,
  },
  translationWarning: {
    marginBottom: 16,
  },
});
